/**
 * src/help/screenKeys.ts — stable help screen keys + a route→key map (WO-H1).
 *
 * Help content is addressed by a STABLE screenKey, decoupled from Expo Router file
 * paths so a screen can be renamed without breaking its help. The header "?" resolves
 * the active route name to a screenKey via ROUTE_TO_SCREEN_KEY.
 *
 * Adding help to a new screen: add a SCREEN_KEYS entry, map its route below, and add a
 * HelpEntry in content.ts with the same screenKey. (Lint in api-types CI rejects unknown keys.)
 */

export const SCREEN_KEYS = {
  home: 'home',
  queue: 'queue',
  estimates: 'estimates',
  estimateDetail: 'estimate.detail',
  estimateNew: 'estimate.new',
  capture: 'capture',
  invoices: 'invoices',
  invoiceDetail: 'invoice.detail',
  review: 'review',
  calendar: 'calendar',
  customers: 'customers',
  voice: 'voice',
  assistant: 'assistant',
  helpCenter: 'help.center',
} as const;

export type ScreenKey = (typeof SCREEN_KEYS)[keyof typeof SCREEN_KEYS];

/** Coachmark flows (first-run guided walkthroughs). Exactly the three critical flows for H1. */
export const FLOW_KEYS = {
  captureReceipt: 'flow.capture-receipt',
  buildEstimate: 'flow.build-estimate',
  sendInvoice: 'flow.send-invoice',
} as const;

export type FlowKey = (typeof FLOW_KEYS)[keyof typeof FLOW_KEYS];

/**
 * Expo Router route name (segment path, no leading group) → screenKey.
 * Used by the global header "?" in app/(app)/_layout.tsx to pick the right entry.
 * Routes absent here fall back to the Help Center.
 */
export const ROUTE_TO_SCREEN_KEY: Record<string, ScreenKey> = {
  '(tabs)/home': SCREEN_KEYS.home,
  '(tabs)/queue': SCREEN_KEYS.queue,
  '(tabs)/estimates': SCREEN_KEYS.estimates,
  '(tabs)/calendar': SCREEN_KEYS.calendar,
  'estimates/new': SCREEN_KEYS.estimateNew,
  'estimates/[id]': SCREEN_KEYS.estimateDetail,
  'capture': SCREEN_KEYS.capture,
  'invoices': SCREEN_KEYS.invoices,
  'invoices/[id]': SCREEN_KEYS.invoiceDetail,
  'review': SCREEN_KEYS.review,
  'customers': SCREEN_KEYS.customers,
  'voice': SCREEN_KEYS.voice,
  'assistant': SCREEN_KEYS.assistant,
  'help': SCREEN_KEYS.helpCenter,
};

export function screenKeyForRoute(routeName: string | undefined): ScreenKey | undefined {
  if (!routeName) return undefined;
  return ROUTE_TO_SCREEN_KEY[routeName];
}
